/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 28/09/23
 */

import { Supervisor } from '@/wpt-lib/interfaces/Supervisor';
import React from 'react';
import { Box, Heading, HStack, Link, Text } from '@chakra-ui/react';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { Tile } from '@/components/Tile';
import { formatFirstName, formatName } from '@/wpt-lib/utils/formatName';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import ListDisplay from '@/components/ListDisplay';
import { getMailBody } from '@/utils/openMail';
import { User } from '@/wpt-lib/interfaces/User';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';

const SupervisorsTile = ({
    assignment,
    supervisors,
    year,
    loading,
    error,
}: {
    assignment?: Assignment;
    supervisors?: Supervisor[];
    year?: number;
    loading: boolean;
    error: boolean;
}) => {
    const { user } = useAssignment();
    const {
        makeRequest,
        loading: localAdminsLoading,
        error: localAdminsError,
        responseData: localAdmins,
    } = useGetRequest<User[]>();

    useAsyncEffect(() => {
        (async () => {
            try {
                if (assignment) await makeRequest(`assignment/${assignment?.id}/localAdmins`);
            } catch (e) {
                /* Handled by hook. */
            }
        })();
    }, [assignment]);

    return (
        <Tile ariaLabelledBy="Supervisor-Heading" colspan={{ base: 2, lg: 1 }} role="landmark">
            <StateDisplay
                loading={loading}
                error={error}
                data={supervisors}
                errorRender={() => <SimpleAlert variant="subtle">Could not load Supervisors</SimpleAlert>}
                render={(supervisors: Supervisor[]) => {
                    let supervisor = supervisors.find((s) => s.isAutoAssigned);
                    if (!supervisor && supervisors.length > 0) supervisor = supervisors[0];
                    return (
                        <HStack align="flex-start" w="full" justify="space-between" gap={5}>
                            <Box>
                                <Heading id="Supervisor-Heading" as="h3" fontSize="md" marginBottom={2}>
                                    My supervisor:
                                </Heading>
                                {supervisor && (
                                    <Box key={supervisor.id}>
                                        <Text>{`${formatName(supervisor.user)}`}</Text>
                                        <Text>{supervisor.user.staffId}</Text>
                                    </Box>
                                )}
                            </Box>
                            <ListDisplay<Supervisor>
                                heading="Alternate Supervisors:"
                                listItems={supervisors
                                    .filter((s) => s.id !== supervisor?.id)
                                    .sort((a, b) => formatName(a.user).localeCompare(formatName(b.user)))}
                                renderItem={(supervisor) => formatName(supervisor.user)}
                                minItems={1}
                                id="id"
                            />
                            <StateDisplay
                                loading={localAdminsLoading}
                                error={!!localAdminsError}
                                data={localAdmins}
                                errorRender={() => (
                                    <SimpleAlert variant="subtle">Could not load local admins</SimpleAlert>
                                )}
                                render={(admins: User[]) => {
                                    return (
                                        <ListDisplay<User>
                                            heading="Local Admins:"
                                            listItems={admins.sort((a, b) =>
                                                formatName(a).localeCompare(formatName(b)),
                                            )}
                                            renderItem={(localAdmin) => {
                                                const email = localAdmin.email ?? '';
                                                const subject = `[WPT] Assistance with Work Plan – ${formatName(user)}`;
                                                const message = `Dear ${formatFirstName(
                                                    localAdmin,
                                                )},%0D%0A%0D%0ACan you please provide some assistance in relation to my ${
                                                    year + ' ' ?? ''
                                                }work plan:`;
                                                return (
                                                    <Link href={getMailBody(subject, email, message)}>
                                                        {formatName(localAdmin)}
                                                    </Link>
                                                );
                                            }}
                                            minItems={0}
                                            placeholder="Not Assigned"
                                            id="id"
                                        />
                                    );
                                }}
                            />
                        </HStack>
                    );
                }}
            />
        </Tile>
    );
};

export default SupervisorsTile;
